import { Injectable } from "@angular/core";
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanLoad,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
} from "@angular/router";
import { OAuthService } from "angular-oauth2-oidc";
import { getCurrentUser } from "aws-amplify/auth";
import { isCognitoEnabled } from "../feature-flags";

@Injectable({
    providedIn: "root",
})
export class CanActivateGuard implements CanActivate, CanLoad {
    constructor(private router: Router, private oauthService: OAuthService) {}

    canActivate(
        _next: ActivatedRouteSnapshot,
        _state: RouterStateSnapshot
    ): Promise<boolean> {
        return this.checkLogin();
    }

    canLoad(_route: Route, _segments: UrlSegment[]): Promise<boolean> {
        return this.checkLogin();
    }

    private async checkLogin(): Promise<boolean> {
        // ALWAYS try Cognito first, regardless of the feature flag. The flag
        // is a *source-of-choice* (which sign-in flow to start) -- not a
        // gate on access for an already-authenticated user. Sprint 6 found a
        // failure mode where the flag was read as false at this moment
        // despite a valid Cognito session existing, bouncing the user back
        // to Duende. Checking the session first eliminates that class of
        // race/clearing issue and is correct in all flag states.
        try {
            await getCurrentUser();
            return true;
        } catch {
            /* no Cognito session -- fall through to Duende check */
        }

        // Duende session check. Previous code called `getIdentityClaims`
        // without parentheses, which always evaluated truthy (function
        // reference); fixed to an actual call.
        if (this.oauthService.getIdentityClaims() !== null) {
            const currentTime = new Date();
            const futureTime = this.oauthService.getAccessTokenExpiration();
            const timeDifference = futureTime
                ? futureTime - currentTime.getTime()
                : 0;
            if (timeDifference > 0) {
                return true;
            }
        }

        // No session of either kind. Route to whichever sign-in flow the
        // flag prefers -- only place the flag still matters.
        this.router.navigate([
            isCognitoEnabled() ? "/auth/sign-in" : "/auth/login",
        ]);
        return false;
    }
}
